import { useState, MouseEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CheckIcon from '@mui/icons-material/Check';
import LanguageIcon from '@mui/icons-material/Language';

import { SUPPORTED_LANGUAGES, setStoredLocale } from '#i18n';
import { setLocale } from '#features/app/config.slice';
import { useUpdateMyLocaleMutation } from '#services/auth';

// Header control letting a user pick their language. Persists the choice to
// localStorage (so it survives reload) and, for logged-in users, to their
// account so server-rendered emails address them in the same language.
function LanguageSwitcher() {
  const { t, i18n } = useTranslation('common');
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(
    (state: { auth: { isLoggedIn: boolean } }) => state.auth.isLoggedIn,
  );
  const [updateMyLocale] = useUpdateMyLocaleMutation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (SUPPORTED_LANGUAGES.length <= 1) {
    return null;
  }

  const current = i18n.language;

  const handleSelect = (lng: string) => {
    setAnchorEl(null);
    if (lng === current) {
      return;
    }
    void i18n.changeLanguage(lng);
    setStoredLocale(lng);
    dispatch(setLocale(lng));
    if (isLoggedIn) {
      // Best-effort persistence; a failure only means emails fall back to the
      // deployment default, so don't surface it to the user.
      void updateMyLocale(lng);
    }
  };

  return (
    <>
      <Tooltip title={t('language.label')}>
        <IconButton
          id="language-button"
          size="large"
          color="inherit"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget)}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ list: { 'aria-labelledby': 'language-button' } }}
      >
        {SUPPORTED_LANGUAGES.map((lng) => (
          <MenuItem
            key={lng}
            selected={lng === current}
            onClick={() => handleSelect(lng)}
          >
            <ListItemIcon>
              {lng === current && <CheckIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{t(`language.${lng}`)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default LanguageSwitcher;
